import { useState } from 'react';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { loadLayout, saveCurrentLayout, deleteLayout, renameLayout, clearAllCards } from '../store/cardsSlice';
import { MdSave, MdEdit, MdDelete, MdCheck, MdClose, MdAdd } from 'react-icons/md';

const LayoutNavBar = () => {
    const dispatch = useAppDispatch();
    const { savedLayouts, activeLayoutId } = useAppSelector((state) => state.cards);
    const [showSaveDialog, setShowSaveDialog] = useState(false);
    const [newLayoutName, setNewLayoutName] = useState('');
    const [editingLayoutId, setEditingLayoutId] = useState<string | null>(null);
    const [editingName, setEditingName] = useState('');

    const handleSaveLayout = () => {
        if (newLayoutName.trim()) {
            dispatch(saveCurrentLayout(newLayoutName.trim()));
            setNewLayoutName('');
            setShowSaveDialog(false);
        }
    };

    const handleCreateEmptyLayout = () => {
        const layoutName = `Layout ${savedLayouts.length + 1}`;
        dispatch(clearAllCards());
        dispatch(saveCurrentLayout(layoutName));
    };

    const handleLoadLayout = (layoutId: string) => {
        dispatch(loadLayout(layoutId));
    };

    const handleDeleteLayout = (layoutId: string, e: React.MouseEvent) => {
        e.stopPropagation();
        if (confirm('Are you sure you want to delete this layout?')) {
            dispatch(deleteLayout(layoutId));
        }
    };

    const startEditing = (layoutId: string, currentName: string, e: React.MouseEvent) => {
        e.stopPropagation();
        setEditingLayoutId(layoutId);
        setEditingName(currentName);
    };

    const handleRename = (layoutId: string) => {
        if (editingName.trim()) {
            dispatch(renameLayout({ id: layoutId, name: editingName.trim() }));
        }
        setEditingLayoutId(null);
        setEditingName('');
    };

    const cancelEditing = () => {
        setEditingLayoutId(null);
        setEditingName('');
    };

    return (
        <div
            style={{
                backgroundColor: 'var(--color-surface)',
                borderBottom: '1px solid var(--color-border-light)',
                padding: 'var(--spacing-sm) var(--spacing-md)',
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--spacing-md)',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
                overflowX: 'auto',
                overflowY: 'hidden',
                minHeight: '48px',
            }}
        >
            {/* Layout Tabs */}
            <div style={{ display: 'flex', gap: 'var(--spacing-sm)', flex: 1, alignItems: 'center' }}>
                {savedLayouts.length === 0 ? (
                    <span style={{
                        fontSize: '0.875rem',
                        color: 'var(--color-text-secondary)',
                        fontStyle: 'italic'
                    }}>
                        No saved layouts yet
                    </span>
                ) : (
                    savedLayouts.map((layout) => (
                        <div
                            key={layout.id}
                            onClick={() => handleLoadLayout(layout.id)}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--spacing-xs)',
                                padding: 'var(--spacing-xs) var(--spacing-sm)',
                                borderRadius: 'var(--radius-md)',
                                backgroundColor: activeLayoutId === layout.id ? 'var(--color-primary-600)' : 'var(--color-gray-100)',
                                color: activeLayoutId === layout.id ? 'white' : 'var(--color-text-primary)',
                                cursor: 'pointer',
                                fontSize: '0.875rem',
                                fontWeight: '500',
                                transition: 'all 200ms ease-in-out',
                                whiteSpace: 'nowrap',
                                border: activeLayoutId === layout.id ? '1px solid var(--color-primary-700)' : '1px solid var(--color-border-light)',
                            }}
                            onMouseEnter={(e) => {
                                if (activeLayoutId !== layout.id) {
                                    e.currentTarget.style.backgroundColor = 'var(--color-gray-200)';
                                }
                            }}
                            onMouseLeave={(e) => {
                                if (activeLayoutId !== layout.id) {
                                    e.currentTarget.style.backgroundColor = 'var(--color-gray-100)';
                                }
                            }}
                        >
                            {editingLayoutId === layout.id ? (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-xs)' }} onClick={(e) => e.stopPropagation()}>
                                    <input
                                        type="text"
                                        value={editingName}
                                        onChange={(e) => setEditingName(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') handleRename(layout.id);
                                            if (e.key === 'Escape') cancelEditing();
                                        }}
                                        autoFocus
                                        style={{
                                            padding: '2px 6px',
                                            fontSize: '0.875rem',
                                            border: '1px solid var(--color-border-light)',
                                            borderRadius: 'var(--radius-sm)',
                                            backgroundColor: 'var(--color-surface)',
                                            color: 'var(--color-text-primary)',
                                            width: '120px',
                                        }}
                                    />
                                    <button
                                        onClick={() => handleRename(layout.id)}
                                        style={{
                                            padding: '2px',
                                            background: 'transparent',
                                            border: 'none',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            color: 'var(--color-success-600)',
                                        }}
                                    >
                                        <MdCheck size={16} />
                                    </button>
                                    <button
                                        onClick={cancelEditing}
                                        style={{
                                            padding: '2px',
                                            background: 'transparent',
                                            border: 'none',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            color: 'var(--color-danger-600)',
                                        }}
                                    >
                                        <MdClose size={16} />
                                    </button>
                                </div>
                            ) : (
                                <>
                                    <span>{layout.name}</span>
                                    <button
                                        onClick={(e) => startEditing(layout.id, layout.name, e)}
                                        style={{
                                            padding: '2px',
                                            background: 'transparent',
                                            border: 'none',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            color: activeLayoutId === layout.id ? 'rgba(255,255,255,0.8)' : 'var(--color-text-secondary)',
                                            opacity: 0.7,
                                        }}
                                        onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; }}
                                        onMouseLeave={(e) => { e.currentTarget.style.opacity = '0.7'; }}
                                    >
                                        <MdEdit size={14} />
                                    </button>
                                    <button
                                        onClick={(e) => handleDeleteLayout(layout.id, e)}
                                        style={{
                                            padding: '2px',
                                            background: 'transparent',
                                            border: 'none',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            color: activeLayoutId === layout.id ? 'rgba(255,255,255,0.8)' : 'var(--color-text-secondary)',
                                            opacity: 0.7,
                                        }}
                                        onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; }}
                                        onMouseLeave={(e) => { e.currentTarget.style.opacity = '0.7'; }}
                                    >
                                        <MdDelete size={14} />
                                    </button>
                                </>
                            )}
                        </div>
                    ))
                )}
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: 'var(--spacing-sm)', alignItems: 'center' }}>
                {/* Add Empty Layout Button */}
                <button
                    onClick={handleCreateEmptyLayout}
                    style={{
                        padding: 'var(--spacing-xs) var(--spacing-sm)',
                        backgroundColor: 'var(--color-gray-100)',
                        color: 'var(--color-text-primary)',
                        border: '1px solid var(--color-border-light)',
                        borderRadius: 'var(--radius-md)',
                        cursor: 'pointer',
                        fontSize: '0.875rem',
                        fontWeight: '500',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--spacing-xs)',
                        whiteSpace: 'nowrap',
                        transition: 'all 200ms ease-in-out',
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = 'var(--color-gray-200)';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'var(--color-gray-100)';
                    }}
                >
                    <MdAdd size={16} />
                    New Layout
                </button>

                {/* Save Layout Button */}
                {showSaveDialog ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)' }}>
                        <input
                            type="text"
                            value={newLayoutName}
                            onChange={(e) => setNewLayoutName(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') handleSaveLayout();
                                if (e.key === 'Escape') setShowSaveDialog(false);
                            }}
                            placeholder="Layout name..."
                            autoFocus
                            style={{
                                padding: 'var(--spacing-xs) var(--spacing-sm)',
                                fontSize: '0.875rem',
                                border: '1px solid var(--color-border-light)',
                                borderRadius: 'var(--radius-md)',
                                backgroundColor: 'var(--color-surface)',
                                color: 'var(--color-text-primary)',
                                width: '150px',
                            }}
                        />
                        <button
                            onClick={handleSaveLayout}
                            disabled={!newLayoutName.trim()}
                            style={{
                                padding: 'var(--spacing-xs) var(--spacing-sm)',
                                backgroundColor: newLayoutName.trim() ? 'var(--color-success-600)' : 'var(--color-gray-300)',
                                color: 'white',
                                border: 'none',
                                borderRadius: 'var(--radius-md)',
                                cursor: newLayoutName.trim() ? 'pointer' : 'not-allowed',
                                fontSize: '0.875rem',
                                fontWeight: '500',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--spacing-xs)',
                            }}
                        >
                            <MdCheck size={16} />
                            Save
                        </button>
                        <button
                            onClick={() => {
                                setShowSaveDialog(false);
                                setNewLayoutName('');
                            }}
                            style={{
                                padding: 'var(--spacing-xs)',
                                backgroundColor: 'transparent',
                                color: 'var(--color-text-secondary)',
                                border: '1px solid var(--color-border-light)',
                                borderRadius: 'var(--radius-md)',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                            }}
                        >
                            <MdClose size={16} />
                        </button>
                    </div>
                ) : (
                    <button
                        onClick={() => setShowSaveDialog(true)}
                        style={{
                            padding: 'var(--spacing-xs) var(--spacing-sm)',
                            backgroundColor: 'var(--color-primary-600)',
                            color: 'white',
                            border: 'none',
                            borderRadius: 'var(--radius-md)',
                            cursor: 'pointer',
                            fontSize: '0.875rem',
                            fontWeight: '500',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--spacing-xs)',
                            whiteSpace: 'nowrap',
                            transition: 'background-color 200ms ease-in-out',
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = 'var(--color-primary-700)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'var(--color-primary-600)';
                        }}
                    >
                        <MdSave size={16} />
                        Save Current Layout
                    </button>
                )}
            </div>
        </div>
    );
};

export default LayoutNavBar;
